import { createContext, ReactNode, use, useMemo, useState } from "react";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { HeatSeries } from "src/store/api/generatedApi";
import { useStringQueryParamState } from "hooks/useStringQueryParamState";
import { useHeatsData } from "./HeatsDataContext";
import { DropGrouping } from "../timelineTab/HeatDropTimeline";

export interface HeatsSelectionContextType {
  selectedHeat: HeatSeries | null;
  selectedHeatId: string | null;
  setSelectedHeatId: (heatId: string | null) => void;
  selectedWatchHeatId: string | null;
  setSelectedWatchHeatId: (heatId: string | null) => void;
  selectedWatchLocation: string | null;
  setSelectedWatchLocation: (location: string | null) => void;
  selectedSummaryName: string;
  setSelectedSummaryName: (name: string) => void;
  selectedTabKey: string;
  setSelectedTabKey: (tabKey: string) => void;
  gridApiRef: ReturnType<typeof useGridApiRef>;
  dropGrouping: DropGrouping;
  setDropGrouping: (grouping: DropGrouping) => void;
}

const HeatsSelectionContext = createContext<
  HeatsSelectionContextType | undefined
>(undefined);

interface HeatsSelectionProviderProps {
  children: ReactNode;
}

export const HeatsSelectionProvider = ({
  children,
}: HeatsSelectionProviderProps) => {
  const { data } = useHeatsData();
  const gridApiRef = useGridApiRef();

  const [selectedHeatId, setSelectedHeatId] = useStringQueryParamState<
    string | null
  >("heat_id", null);

  const [selectedWatchHeatId, setSelectedWatchHeatId] =
    useStringQueryParamState<string | null>("watch_heat_id", null);

  const [selectedWatchLocation, setSelectedWatchLocation] = useState<
    string | null
  >(null);

  const [selectedSummaryName, setSelectedSummaryName] = useState("");

  const [selectedTabKey, setSelectedTabKey] = useStringQueryParamState<string>(
    "heats_tab",
    "mixDetails"
  );

  const [dropGrouping, setDropGrouping] = useState<DropGrouping>("combined");

  const effectiveSummaryName =
    selectedSummaryName !== ""
      ? selectedSummaryName
      : (Object.keys(data?.available_mix_summaries ?? {}).sort()[0] ?? "");

  const selectedHeat = useMemo(
    () => data?.series.find((heat) => heat.heat_id === selectedHeatId) ?? null,
    [data, selectedHeatId]
  );

  const value: HeatsSelectionContextType = useMemo(
    () => ({
      selectedHeat,
      selectedHeatId,
      setSelectedHeatId,
      selectedWatchHeatId,
      setSelectedWatchHeatId,
      selectedWatchLocation,
      setSelectedWatchLocation,
      selectedSummaryName: effectiveSummaryName,
      setSelectedSummaryName,
      selectedTabKey,
      setSelectedTabKey,
      gridApiRef,
      dropGrouping,
      setDropGrouping,
    }),
    [
      selectedHeat,
      selectedHeatId,
      setSelectedHeatId,
      selectedWatchHeatId,
      setSelectedWatchHeatId,
      selectedWatchLocation,
      effectiveSummaryName,
      selectedTabKey,
      setSelectedTabKey,
      gridApiRef,
      dropGrouping,
    ]
  );

  return (
    <HeatsSelectionContext value={value}>{children}</HeatsSelectionContext>
  );
};

export const useHeatsSelection = (): HeatsSelectionContextType => {
  const context = use(HeatsSelectionContext);
  if (context === undefined) {
    throw new Error(
      "useHeatsSelection must be used within a HeatsSelectionProvider"
    );
  }
  return context;
};
